import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { apiClient } from '@/lib/api-client';
import { useAuth } from '@/contexts/AuthContext';
import { useUserInitRecentlyPlayed } from '@/hooks/queries/useUserInitQuery';

interface RecentlyPlayedGame {
  id: string;
  title: string;
  image: string;
  provider?: string;
  lastPlayed: Date;
}

interface GameInfo {
  id: string;
  title: string;
  image: string;
  provider?: string;
}

interface RecentlyPlayedContextType {
  recentlyPlayedGames: RecentlyPlayedGame[];
  addRecentlyPlayed: (game: GameInfo) => Promise<void>;
  getRecentlyPlayedList: () => RecentlyPlayedGame[];
  isLoading: boolean;
}

const RecentlyPlayedContext = createContext<RecentlyPlayedContextType | undefined>(undefined);

export const useRecentlyPlayed = () => {
  const context = useContext(RecentlyPlayedContext);
  if (!context) {
    throw new Error('useRecentlyPlayed must be used within a RecentlyPlayedProvider');
  }
  return context;
};

export const RecentlyPlayedProvider = ({ children }: { children: ReactNode }) => {
  const [recentlyPlayedGames, setRecentlyPlayedGames] = useState<RecentlyPlayedGame[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const { user } = useAuth();

  // Get recently played from unified /user/init response (no extra API call)
  const initRecentlyPlayed = useUserInitRecentlyPlayed();

  // Populate from /user/init data
  useEffect(() => {
    if (!user) {
      setRecentlyPlayedGames([]);
      setIsLoading(false);
      return;
    }

    if (initRecentlyPlayed && initRecentlyPlayed.length > 0) {
      const games: RecentlyPlayedGame[] = initRecentlyPlayed.map((item) => ({
        id: item.game_id,
        title: item.game_title,
        image: item.game_image || '',
        provider: item.game_provider || undefined,
        lastPlayed: new Date(item.last_played_at),
      }));
      setRecentlyPlayedGames(games);
    }
    setIsLoading(false);
  }, [user, initRecentlyPlayed]);

  // Add a game to recently played with full game metadata
  const addRecentlyPlayed = useCallback(async (game: GameInfo) => {
    const newGame: RecentlyPlayedGame = {
      id: game.id,
      title: game.title,
      image: game.image,
      provider: game.provider,
      lastPlayed: new Date(),
    };

    // Update local state immediately
    setRecentlyPlayedGames(prevGames => {
      const filteredGames = prevGames.filter(g => g.id !== newGame.id);
      const updatedGames = [newGame, ...filteredGames];
      return updatedGames.slice(0, 10);
    });

    // Save to backend if user is authenticated
    try {
      if (!user) return;

      const data = await apiClient.post('/recently-played', {
        gameId: game.id,
        gameTitle: game.title,
        gameImage: game.image,
        gameProvider: game.provider,
      });

      if (!data?.success) {
        console.error('Error saving recently played game:', data?.error);
      }
    } catch (error) {
      console.error('Error saving recently played game:', error);
    }
  }, [user]);

  const getRecentlyPlayedList = useCallback(() => {
    return recentlyPlayedGames.slice(0, 6);
  }, [recentlyPlayedGames]);

  return (
    <RecentlyPlayedContext.Provider value={{
      recentlyPlayedGames,
      addRecentlyPlayed,
      getRecentlyPlayedList,
      isLoading,
    }}>
      {children}
    </RecentlyPlayedContext.Provider>
  );
};
